import type { LessonProgress, LessonStatus, ProgressState } from '../domain/progress'
import { getDefaultStore, type KeyValueStore } from './storage'

/**
 * Progress Repository
 * -------------------
 * The single boundary between the app and persisted learner state. Everything
 * above it works with the {@link ProgressState} domain type; only this module
 * knows the storage key, JSON encoding, and migration strategy.
 *
 * The format is versioned so that a published schema can evolve without losing
 * a learner's data. `migrate()` upgrades older documents on load.
 */

export const PROGRESS_VERSION = 1
const STORAGE_KEY = 'daily-learning/progress/v1'

export function defaultProgressState(): ProgressState {
  return {
    version: PROGRESS_VERSION,
    preferences: {
      lessonDurationMinutes: 9,
      reducedMotion: 'system',
    },
    lessons: {},
    currentLessonId: null,
  }
}

export interface ProgressRepository {
  /** Read the current state, applying migrations and healing corrupt data. */
  load(): ProgressState
  /** Persist a full state snapshot. */
  save(state: ProgressState): void
  /** Update a single lesson's progress and return the new state. */
  setLessonStatus(lessonId: string, status: LessonStatus): ProgressState
  /** Read a single lesson's stored progress, if any. */
  getLessonProgress(lessonId: string): LessonProgress | undefined
  /** Persist one interaction's response under a lesson and return the new state. */
  setLessonResponse(lessonId: string, interactionId: string, response: unknown): ProgressState
  /** Mark one interaction complete under a lesson and return the new state. */
  setInteractionComplete(lessonId: string, interactionId: string): ProgressState
  /** Serialize the full state as pretty JSON for the export feature. */
  export(): string
  /** Replace state from previously exported JSON. Throws on invalid input. */
  import(json: string): ProgressState
  /** Clear all persisted state, returning fresh defaults. */
  reset(): ProgressState
}

/**
 * Upgrade a raw parsed document to the current version. Add cases here as the
 * schema evolves; unknown/older shapes fall back to defaults rather than crash.
 */
function migrate(raw: unknown): ProgressState {
  if (!isRecord(raw) || typeof raw.version !== 'number') {
    return defaultProgressState()
  }

  // Future migrations, e.g. `if (state.version < 2) { ... }`, go here.
  // For now we only support v1, so anything else resets to a safe default.
  if (raw.version !== PROGRESS_VERSION) {
    return defaultProgressState()
  }

  return normalize(raw)
}

/** Merge a parsed document over defaults so missing fields are always present. */
function normalize(raw: Record<string, unknown>): ProgressState {
  const base = defaultProgressState()
  const preferences = isRecord(raw.preferences) ? raw.preferences : {}
  const lessons = isRecord(raw.lessons) ? raw.lessons : {}

  return {
    version: PROGRESS_VERSION,
    preferences: {
      lessonDurationMinutes:
        typeof preferences.lessonDurationMinutes === 'number'
          ? preferences.lessonDurationMinutes
          : base.preferences.lessonDurationMinutes,
      reducedMotion:
        preferences.reducedMotion === 'reduce' ||
        preferences.reducedMotion === 'no-preference' ||
        preferences.reducedMotion === 'system'
          ? preferences.reducedMotion
          : base.preferences.reducedMotion,
    },
    lessons: normalizeLessons(lessons),
    currentLessonId: typeof raw.currentLessonId === 'string' ? raw.currentLessonId : null,
  }
}

function normalizeLessons(raw: Record<string, unknown>): Record<string, LessonProgress> {
  const out: Record<string, LessonProgress> = {}
  for (const [id, value] of Object.entries(raw)) {
    if (!isRecord(value)) continue
    const status = value.status
    if (status !== 'not-started' && status !== 'in-progress' && status !== 'completed') continue
    out[id] = {
      lessonId: id,
      status,
      updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : new Date(0).toISOString(),
      responses: isRecord(value.responses) ? value.responses : {},
      completedInteractions: Array.isArray(value.completedInteractions)
        ? value.completedInteractions.filter((entry): entry is string => typeof entry === 'string')
        : [],
    }
  }
  return out
}

export function createProgressRepository(
  store: KeyValueStore = getDefaultStore(),
): ProgressRepository {
  function load(): ProgressState {
    const raw = store.get(STORAGE_KEY)
    if (raw === null) return defaultProgressState()
    try {
      return migrate(JSON.parse(raw))
    } catch {
      // Corrupt JSON: reset rather than trap the learner in a broken state.
      return defaultProgressState()
    }
  }

  function save(state: ProgressState): void {
    store.set(STORAGE_KEY, JSON.stringify(state))
  }

  /** Merge a patch into one lesson's progress, preserving all other fields. */
  function writeLesson(
    lessonId: string,
    patch: Partial<Omit<LessonProgress, 'lessonId'>>,
    status: LessonStatus,
  ): ProgressState {
    const state = load()
    const existing = state.lessons[lessonId]
    const merged: LessonProgress = {
      lessonId,
      status,
      updatedAt: new Date().toISOString(),
      responses: existing?.responses ?? {},
      completedInteractions: existing?.completedInteractions ?? [],
      ...patch,
    }
    const next: ProgressState = {
      ...state,
      currentLessonId: status === 'completed' ? state.currentLessonId : lessonId,
      lessons: { ...state.lessons, [lessonId]: merged },
    }
    save(next)
    return next
  }

  function setLessonStatus(lessonId: string, status: LessonStatus): ProgressState {
    return writeLesson(lessonId, {}, status)
  }

  function getLessonProgress(lessonId: string): LessonProgress | undefined {
    return load().lessons[lessonId]
  }

  function keepOpen(lessonId: string): LessonStatus {
    return load().lessons[lessonId]?.status === 'completed' ? 'completed' : 'in-progress'
  }

  function setLessonResponse(
    lessonId: string,
    interactionId: string,
    response: unknown,
  ): ProgressState {
    const existing = load().lessons[lessonId]
    return writeLesson(
      lessonId,
      { responses: { ...(existing?.responses ?? {}), [interactionId]: response } },
      keepOpen(lessonId),
    )
  }

  function setInteractionComplete(lessonId: string, interactionId: string): ProgressState {
    const existing = load().lessons[lessonId]
    const done = existing?.completedInteractions ?? []
    const completedInteractions = done.includes(interactionId) ? done : [...done, interactionId]
    return writeLesson(lessonId, { completedInteractions }, keepOpen(lessonId))
  }

  function exportJson(): string {
    return JSON.stringify(load(), null, 2)
  }

  function importJson(json: string): ProgressState {
    const parsed = JSON.parse(json) as unknown
    const migrated = migrate(parsed)
    save(migrated)
    return migrated
  }

  function reset(): ProgressState {
    store.remove(STORAGE_KEY)
    const fresh = defaultProgressState()
    save(fresh)
    return fresh
  }

  return {
    load,
    save,
    setLessonStatus,
    getLessonProgress,
    setLessonResponse,
    setInteractionComplete,
    export: exportJson,
    import: importJson,
    reset,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Shared app-wide repository instance backed by the default store. */
export const progressRepository = createProgressRepository()
